import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../utils/AuthContext';
import { Box, Heading, Spinner, Alert, AlertIcon, Button, Flex, VStack, Select, Input } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AssignPaperSet = () => {
  const [users, setUsers] = useState([]);
  const [paperSets, setPaperSets] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState('');
  const [examDate, setExamDate] = useState('');
  const { auth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsersAndPaperSets = async () => {
      setLoading(true);
      try {
        const usersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/users`, {
          headers: {
            Authorization: `Bearer ${auth.token}`
          }
        });
        const activeUsers = (usersResponse.data.$values || usersResponse.data).filter(user => user.isActive);
        setUsers(activeUsers);

        const paperSetsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/PaperSet`, {
          headers: {
            Authorization: `Bearer ${auth.token}`
          }
        });
        const activePaperSets = (paperSetsResponse.data.$values || paperSetsResponse.data).filter(ps => ps.isActive);
        setPaperSets(activePaperSets);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error fetching data');
        setLoading(false);
      }
    };

    fetchUsersAndPaperSets();
  }, [auth.token]);

  const handleAssignPaperSet = async () => {
    if (selectedUsers.length === 0 || !selectedPaperSetId || !examDate) {
      alert('Please select users, a paper set, and an exam date.');
      return;
    }

    try {
      await Promise.all(
        selectedUsers.map(async (user) => {
          await axios.post(`${process.env.REACT_APP_API_URL}/api/PaperSet/Assign`, {
            userId: user.userId,
            paperSetId: selectedPaperSetId,
            examDate: new Date(examDate)
          }, {
            headers: {
              Authorization: `Bearer ${auth.token}`
            }
          });
        })
      );
      alert('Paper set assigned successfully.');
      setSelectedUsers([]);
      setSelectedPaperSetId('');
      setExamDate('');
    } catch (error) {
      console.error('Error assigning paper set:', error.response ? error.response.data : error.message);
      alert(`Failed to assign paper set: ${error.response ? error.response.data : error.message}`);
    }
  };

  const onSelectionChanged = (event) => {
    setSelectedUsers(event.api.getSelectedRows());
  };

  const userColumnDefs = [
    { headerName: '', checkboxSelection: true, width: 50 },
    { headerName: 'Username', field: 'username', filter: true },
    { headerName: 'Role', field: 'role', filter: true },
    { headerName: 'Name', field: 'userinfo.name', filter: true },
    { headerName: 'Email', field: 'userinfo.email', filter: true },
    { headerName: 'City', field: 'userinfo.city', filter: true },
    { headerName: 'Designation', field: 'userinfo.designation', filter: true },
    { headerName: 'Department', field: 'userinfo.department', filter: true },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8}>
      <Heading mb={6}>Assign Paper Set to Users</Heading>
      <Flex mb={4} justifyContent="space-between" alignItems="center" gap={4}>
        <Select placeholder="Select Paper Set" value={selectedPaperSetId} onChange={(e) => setSelectedPaperSetId(e.target.value)} width="300px">
          <option value="" disabled>-- Select Paper Set --</option>
          {paperSets.map((ps) => (
            <option key={ps.paperSetId} value={ps.paperSetId}>
              {ps.title} (Questions: {ps.numberOfQuestions})
            </option>
          ))}
        </Select>
        <Input
          type="date"
          value={examDate}
          onChange={(e) => setExamDate(e.target.value)}
          placeholder="Select Exam Date"
          width="200px"
        />
        <Button colorScheme="blue" onClick={handleAssignPaperSet}>Assign Paper Set</Button>
      </Flex>
      <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
        <AgGridReact
          rowData={users}
          columnDefs={userColumnDefs}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </Box>
  );
};

export default AssignPaperSet;
